import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SocketService } from './socket.service';
import { Subject, Observable, firstValueFrom } from 'rxjs';
import { User } from 'src/app/shared/models/user.model';
/**
 * Service for user
 */
@Injectable({ providedIn: 'root' })
/**
 * UsersService
 * @class UsersService
 */
export class UsersService {
  /**
   * inject servce
   * @param {HttpClient} http
   */
  private http = inject(HttpClient);
  /**
   * inject service socket
   * @param {SocketService} socket
   */
  private socketService = inject(SocketService);
  /**
   * the url of api
   * @type {string}
   */
  private baseApiUrl = environment.apiUrl + 'company/user';
  /**
   * the subject socket
   * @type {Subject<IuserWithType>}
   */
  private socketUsers$: Subject<IuserWithType> = new Subject();
  /**
   * the echo instance
   * @type {any}
   */
  private echoInstance = null;



  /**
   * Starts listening to socket for users events
   * Subscribe to:
   * - UserAddedEvent: add a user to the list
   * - UserUpdatedEvent: update a user in the list
   * - UserUpdatedForGroupEvent: update a user in the group list
   * - UserChangedToManagerEvent: change the manager of a company
   * - UserDeletedEvent: delete a user from the list
   * - UserDeletedAllEvent: delete all users from the list
   * - UserAddedForGroupEvent: add a user to a group
   * - UserDeletedForGroupEvent: delete a user from a group
   * - UpdateLastActivityEvent: update the last activity of a user
   * @description
   * This function starts the socket for the users service
   * and sets up event listeners for various user-related events.
   * The socketUsers$ observable is updated with the new values.
   */
  startUsersSocket() {
    this.echoInstance = this.socketService.getEchoInstance();
    this.echoInstance.private(`company.${this.socketService.companyId}.users`)
      .listen('.UserAddedEvent', (event) => {
        this.socketUsers$.next({ action: 'add', user: event.user });
      })
      .listen('.UserUpdatedEvent', (event) => {
        this.socketUsers$.next({ action: 'update', user: event.user });
      })
      .listen('.UserUpdatedForGroupEvent', (event) => {
        this.socketUsers$.next({ action: 'updateGroup', users: event.users });
      })
      .listen('.UserChangedToManagerEvent', (event) => {
        this.socketUsers$.next({ action: 'changeManager', user: event.newManager });
      })
      .listen('.UserDeletedEvent', (event) => {
        this.socketUsers$.next({ action: 'delete', users: event.users });
      })
      .listen('.UserDeletedAllEvent', (event) => {
        this.socketUsers$.next({ action: 'deleteALL' });
      })
      .listen('.UserAddedForGroupEvent', (event) => {
        this.socketUsers$.next({ action: 'addGroup', users: event.users });
      })
      .listen('.UserDeletedForGroupEvent', (event) => {
        this.socketUsers$.next({ action: 'deleteGroup', users: event.users });
      })
      .listen('.UpdateLastActivityEvent', (event) => {
        this.socketUsers$.next({ action: 'updateActivity', user: event.user });
      });
  }
  /**
   * Stops listening to socket for users events
   */
  stopUsersSockets() {
    if (this.echoInstance) {
      this.echoInstance.leave(`company.${this.socketService.companyId}.users`);
    }
  }
  /**
   * subscribe to the socket users
   * @returns {Observable<IuserWithType>}
   */
  subSocketUsers(): Observable<IuserWithType> {
    return this.socketUsers$;
  }
  /**
   * get users list
   * @param filters
   * @returns
   */
  getUsers(filters): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl, { params: filters }));
  }
  /**
   * add new users to the list
   * @param data
   * @returns
   */
  addUsers(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + 's', data));
  }
  /**
   * add one user
   * @param data
   * @returns
   */
  addUser(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl, data));
  }
  /**
   * update existing user
   * @param data
   * @returns
   */
  updateUser(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl, data));
  }
  /**
   * delete existing user
   * @param data
   * @returns
   */
  deleteUser(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl, { body: data }));
  }
  /**
   * delete all users
   * @param data
   * @returns
   */
  deleteAllUsers(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl + '/all', { body: data }));
  }
  /**
   * get last backup
   * @param data
   * @returns
   */
  getLastBackup(data): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/backups', { params: data }));
  }
  /**
   * download backup
   * @param data
   * @returns
   */
  downLoadBackupFile(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/backup/download', data, { responseType: 'blob' }));
  }
  /**
   * reset client security keys
   * @param data
   * @returns
   */
  resetClientSecuritykeys(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/reset/securitykeys', data));
  }
  /**
   * reset client 2FA
   * @param data
   * @returns
   */
  resetClient2FA(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/reset/twofactor', data));
  }
  /**
   * reset client security questions
   * @param data
   * @returns
   */
  resetClientSecQuestions(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl + '/reset/questions', data));
  }
  /**
   * get all users that can be owner
   * @returns  list of users
   */
  getUserCanBeOwner(): Promise<any> {
    return firstValueFrom(this.http.get(this.baseApiUrl + '/all'));
  }
  /**
   * delete multiple users
   * @param data
   * @returns
   */
  deleteUsers(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl + 's', { body: data }));
  }
  /**
   * delete users using excel template
   * @param data
   * @returns
   */
  deleteUserWithExcel(data): Promise<any> {
    return firstValueFrom(this.http.request('delete', this.baseApiUrl + '/template', { body: data }));
  }
  /**
   * export all users
   * @param data
   * @returns
   */
  exportAll(data): Promise<any> {
    return firstValueFrom(this.http.post(this.baseApiUrl + '/export/all', data));
  }

}

/**
 * Interface for users service
 * @interface IuserWithType
 */
export interface IuserWithType {
  /**
   * user
   * @type {User} {@link User}
   */
  user?: User;

  users?: User[] | number[];

  /**
   * action
   * @type {string}
   */
  action: 'add' | 'update' | 'delete' | 'deleteALL' | 'changeManager' | 'updateGroup' | 'addGroup' | 'deleteGroup' | 'updateActivity';
}
